/**
 * Provider Mode Selector Component
 *
 * Allows selecting between local (Ollama), API (cloud), auto, or hybrid provider modes.
 */
import { clsx } from 'clsx'
import {
  ServerIcon,
  CloudIcon,
  BoltIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { AgentProviderMode, ProviderStatus } from '@/types/unified'

interface ProviderModeSelectorProps {
  value: AgentProviderMode
  onChange: (mode: AgentProviderMode) => void
  providerStatus?: ProviderStatus
  disabled?: boolean
  compact?: boolean
  className?: string
}

const providerModeConfig = {
  [AgentProviderMode.LOCAL]: {
    label: 'Local',
    description: 'Use Ollama models only',
    icon: ServerIcon,
    color: 'text-green-400',
    bgColor: 'bg-green-900/30',
    borderColor: 'border-green-600',
  },
  [AgentProviderMode.API]: {
    label: 'API',
    description: 'Use cloud APIs (OpenAI, Anthropic, Google)',
    icon: CloudIcon,
    color: 'text-blue-400',
    bgColor: 'bg-blue-900/30',
    borderColor: 'border-blue-600',
  },
  [AgentProviderMode.AUTO]: {
    label: 'Auto',
    description: 'Prefer local, fallback to API',
    icon: BoltIcon,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/30',
    borderColor: 'border-yellow-600',
  },
  [AgentProviderMode.HYBRID]: {
    label: 'Hybrid',
    description: 'Local for simple, API for complex',
    icon: ArrowsRightLeftIcon,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/30',
    borderColor: 'border-purple-600',
  },
}

export default function ProviderModeSelector({
  value,
  onChange,
  providerStatus,
  disabled = false,
  compact = false,
  className,
}: ProviderModeSelectorProps) {
  const modes = Object.values(AgentProviderMode)

  if (compact) {
    return (
      <div className={clsx('flex rounded-lg bg-gray-800 p-1', className)}>
        {modes.map((mode) => {
          const config = providerModeConfig[mode]
          const Icon = config.icon
          const isSelected = value === mode

          return (
            <button
              key={mode}
              onClick={() => onChange(mode)}
              disabled={disabled}
              title={config.description}
              className={clsx(
                'flex items-center px-3 py-1.5 text-sm rounded-md transition-colors',
                isSelected
                  ? `${config.bgColor} ${config.color} border ${config.borderColor}`
                  : 'text-gray-400 hover:text-white hover:bg-gray-700',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Icon className="w-4 h-4 mr-1.5" />
              {config.label}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-300">Provider Mode</label>
        {providerStatus && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Available:</span>
            {providerStatus.local && (
              <span className="flex items-center text-green-400">
                <CheckCircleIcon className="w-3 h-3 mr-0.5" />
                Local
              </span>
            )}
            {providerStatus.anthropic && (
              <span className="flex items-center text-blue-400">
                <CheckCircleIcon className="w-3 h-3 mr-0.5" />
                Anthropic
              </span>
            )}
            {providerStatus.openai && (
              <span className="flex items-center text-teal-400">
                <CheckCircleIcon className="w-3 h-3 mr-0.5" />
                OpenAI
              </span>
            )}
            {!providerStatus.local && !providerStatus.anthropic && !providerStatus.openai && (
              <span className="flex items-center text-red-400">
                <XCircleIcon className="w-3 h-3 mr-0.5" />
                None
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {modes.map((mode) => {
          const config = providerModeConfig[mode]
          const Icon = config.icon
          const isSelected = value === mode

          return (
            <button
              key={mode}
              onClick={() => onChange(mode)}
              disabled={disabled}
              className={clsx(
                'flex flex-col items-start p-4 rounded-lg border-2 transition-all',
                isSelected
                  ? `${config.bgColor} ${config.borderColor}`
                  : 'bg-gray-800 border-gray-700 hover:border-gray-600',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-center mb-2">
                <Icon className={clsx('w-5 h-5 mr-2', isSelected ? config.color : 'text-gray-400')} />
                <span className={clsx('font-medium', isSelected ? 'text-white' : 'text-gray-300')}>
                  {config.label}
                </span>
              </div>
              <p className="text-xs text-gray-400 text-left">{config.description}</p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
